/**
 * <s-product-gallery> — one large slide at a time with a thumbnail strip.
 *
 * Contract:
 *   - Contains the scrolling [data-product-gallery] whose slides carry data-media-id.
 *   - Thumbnail buttons use commandfor="<this id>" command="--show-media" and data-thumb-media-id.
 *   - The thumbnail for the slide in view gets aria-current="true".
 */
class SProductGallery extends HTMLElement {
  #ac = null;
  #observer = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const gallery = this.querySelector('[data-product-gallery]');
    if (!gallery) return;

    this.addEventListener('command', (event) => {
      if (event.command !== '--show-media') return;
      const mediaId = event.source?.dataset?.thumbMediaId;
      if (!mediaId) return;
      const slide = gallery.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
      if (!slide) return;
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const left = gallery.scrollLeft + slide.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
      gallery.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.#setActive(mediaId);
    }, { signal: this.#ac.signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#setActive(entry.target.dataset.mediaId);
      }
    }, { root: gallery, threshold: 0.6 });
    gallery.querySelectorAll('[data-media-id]').forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #setActive(mediaId) {
    for (const thumb of this.querySelectorAll('[data-thumb-media-id]')) {
      if (thumb.dataset.thumbMediaId !== mediaId) {
        thumb.removeAttribute('aria-current');
        continue;
      }
      thumb.setAttribute('aria-current', 'true');
      const strip = thumb.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2 });
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
